import { useZero } from "@rocicorp/zero/react";
import { getInitialConnectorValues } from "../../../shared/connectors/getInitialConnectorValues";
import { sharedMutators } from "../../../shared/mutators";
import {
  type ConnectorConfig,
  connectorVariants,
} from "../../lib/connectorDirectory";
import { connectorAuthTypeLabel } from "../../lib/connector";
import { Dropdown } from "../Dropdown";
import { Icon } from "../Icon";
import { DropdownOpener } from "../Settings/shared";
import { Text } from "../Text";
import { InstructionsStep, StepHeading } from "./InstructionsStep";
import { useRenderWithPlaceholders } from "./renderWithPlaceholders";

type Props = {
  appId: string;
  appConfig: ConnectorConfig;
};

export function VariantPicker({ appId, appConfig }: Props) {
  const z = useZero();
  const renderWithPlaceholders = useRenderWithPlaceholders();
  const variants = connectorVariants(appConfig);

  // A variant is a different definition with its own credential fields, so
  // switching resets the draft to that definition's initial values.
  const switchTo = (variant: ConnectorConfig) => {
    if (variant.identifier === appConfig.identifier) return;
    const initialValues = getInitialConnectorValues(
      variant.definition,
      renderWithPlaceholders
    );
    z.mutate(
      sharedMutators.switchConnectorVariant({
        connectedAppId: appId,
        identifier: variant.identifier,
        name: initialValues.name,
        urlPattern: initialValues.urlPattern,
        configuration: initialValues.configuration,
        createOAuthConnection: initialValues.createOAuthConnection,
      })
    );
  };

  return (
    <InstructionsStep style={{ marginBottom: 24 }}>
      <StepHeading>
        <Text variant="text-regular-md">Connect with</Text>
      </StepHeading>
      <Dropdown
        opener={
          <DropdownOpener type="button">
            <Text variant="text-regular">
              {connectorAuthTypeLabel(appConfig)}
            </Text>
            <span style={{ opacity: 0.3 }}>
              <Icon name="chevron-down" size={12} />
            </span>
          </DropdownOpener>
        }
        align="bottom-left"
        sections={[
          {
            items: variants.map((variant) => ({
              type: "button" as const,
              left: null,
              label: connectorAuthTypeLabel(variant),
              right:
                variant.identifier === appConfig.identifier ? (
                  <Icon name="check" size={12} color="var(--text-secondary)" />
                ) : undefined,
              onClick: () => switchTo(variant),
            })),
          },
        ]}
      />
    </InstructionsStep>
  );
}
